import { getTenantSlugFromHost } from '@/shared/lib/tenant-host'

/**
 * Where the tenant-facing Public API lives, derived from the current frontend
 * host. Shared by the developer portal (curl examples, OpenAPI links) and the
 * Power BI connect dialog (dataset URLs) so both always print the same host.
 */

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')
}

/**
 * The `api.*` host for the current frontend host.
 *
 * iWarranty frontends mirror the API host by first DNS label
 * (`portal.launchpad.iwarranty.ai` -> `api.launchpad.iwarranty.ai`). With
 * per-tenant subdomains the leftmost label is the tenant slug, so we strip it
 * first and swap the env label that follows - `acme.portal.launchpad.iwarranty.ai`
 * still resolves to `api.launchpad.iwarranty.ai`, not `api.portal.launchpad...`.
 * Returns null when there is no env label to swap (single-label / local hosts).
 */
export function apiHostFor(hostname: string): string | null {
  const slug = getTenantSlugFromHost(hostname)
  const base = slug ? hostname.slice(slug.length + 1) : hostname
  const parts = base.split('.')
  if (parts.length < 2) return null
  parts[0] = 'api'
  return parts.join('.')
}

/**
 * Build a Public API URL for a browser fetch.
 *
 * Dev (localhost): a relative path - Vite proxies /api to the local backend.
 * Prod: frontend and backend are separate origins, so build an absolute URL
 * against the api.* host.
 */
export function buildPublicApiUrl(path: string, hostname: string): string {
  if (isLocalHost(hostname)) return path
  const host = apiHostFor(hostname)
  if (!host) return path
  return `https://${host}${path}`
}

/**
 * The API base shown in docs examples. Local dev has no real tenant hostname,
 * so it falls back to a placeholder the reader substitutes.
 */
export function resolveDocsApiBase(hostname: string): string {
  if (isLocalHost(hostname)) return 'https://<your-tenant-host>'
  const host = apiHostFor(hostname)
  return host ? `https://${host}` : 'https://<your-tenant-host>'
}

/**
 * An absolute Public API URL to paste into an external tool (Power BI), which
 * cannot follow a relative path. Local dev uses the page's own origin, where
 * the dev proxy serves /api.
 */
export function absolutePublicApiUrl(path: string, location: Location): string {
  if (isLocalHost(location.hostname)) return `${location.origin}${path}`
  const host = apiHostFor(location.hostname)
  return host ? `https://${host}${path}` : `${location.origin}${path}`
}
